import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Grid, Link, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { useState, ReactNode, useCallback, FC, useEffect, ChangeEvent } from 'react';
import { WsColors, Label } from 'wisestep-base';

const LinkTag = styled(Link)(() => ({
  color: WsColors.TheBlues500,
  cursor: 'pointer',
}));

export interface ISkillsType {
  skill: string;
  score: number;
  yearsUsed?: number;
}

interface ISkillsField {
  initialValue: ISkillsType[];
  onInputChange(args: ISkillsType[]): void;
  labelText?: string;
}

const SkillsField: FC<ISkillsField> = ({
  initialValue = [{ skill: '', yearsUsed: 0, score: 0 }],
  onInputChange,
  labelText = 'Skills',
}) => {
  const [linksValue, setLinksValue] = useState<ISkillsType[]>(initialValue);
  const [totalLinks, setTotalLinks] = useState<ReactNode[]>([]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number, field: 'skill' | 'yearsUsed') => {
      setLinksValue((linksValue: ISkillsType[]) => {
        const result: any[] = linksValue;
        result[index][field] =
          field === 'yearsUsed' ? parseInt(event.target.value) : event.target.value;
        const newValues = [...result];
        onInputChange(result);
        return newValues;
      });
    },
    [onInputChange]
  );

  const addSkillInput = () => {
    if (isEmpty(totalLinks)) {
      setTotalLinks([skillInputWithRemoveIcon(0)]);
    } else {
      setTotalLinks([...totalLinks, skillInputWithRemoveIcon(totalLinks.length)]);
    }
    setLinksValue([...linksValue, { skill: '', score: 0, yearsUsed: 0 }]);
  };

  const removeSkillLink = useCallback(
    (index: number) => {
      setTotalLinks((totalLinks) => {
        delete totalLinks[index];
        return [...totalLinks];
      });
      setLinksValue((linksValue) => {
        delete linksValue[index + 1];
        const newValues = [...linksValue];
        onInputChange(newValues);
        return newValues;
      });
    },
    [onInputChange]
  );

  const skillInputWithRemoveIcon = useCallback(
    (index: number) => (
      <Grid item xs={12} key={index}>
        <TextField
          sx={{ mt: '12px', width: '60%' }}
          defaultValue={linksValue[index + 1]?.skill}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder="Skill"
          variant="outlined"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'skill')}
          autoComplete="candidate_platform"
        />
        <TextField
          sx={{ mt: '12px', width: '30%' }}
          defaultValue={linksValue[index + 1]?.yearsUsed}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder="Score"
          variant="outlined"
          type="number"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'yearsUsed')}
          autoComplete="candidate_messenger_id"
        />
        <RemoveCircleOutlineIcon
          onClick={() => removeSkillLink(index)}
          sx={{
            color: WsColors.Red400,
            mt: '14px',
            pl: '20px',
            cursor: 'pointer',
            fontSize: '42px',
          }}
        />
      </Grid>
    ),
    [handleChange, linksValue, removeSkillLink]
  );

  useEffect(() => {
    if (!isEmpty(linksValue) && isEmpty(totalLinks)) {
      const links: ReactNode[] = [];
      linksValue.forEach((link: ISkillsType, index: number) => {
        index !== 0 && links.push(skillInputWithRemoveIcon(index - 1));
      });
      if (!isEmpty(links)) {
        setTotalLinks([...links]);
      }
    }
  }, [linksValue, skillInputWithRemoveIcon, totalLinks]);

  return (
    <Grid container direction="row">
      <Grid item xs={12}>
        <Label>{labelText}</Label>
        <TextField
          sx={{ width: '60%' }}
          value={linksValue[0]?.skill}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder="Skill"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'skill')}
          variant="outlined"
          autoComplete="candidate_platform"
        />
        <TextField
          sx={{ width: '40%' }}
          value={linksValue[0]?.yearsUsed}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder="Years of experience"
          type="number"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'yearsUsed')}
          variant="outlined"
          autoComplete="candidate_messenger_id"
        />
      </Grid>
      {totalLinks.map((component: ReactNode) => component)}
      <Grid item xs={12}>
        <LinkTag underline="none" onClick={() => addSkillInput()}>
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <AddIcon sx={{ height: '20px', mt: '7px' }} />
            </Grid>
            <Grid item>Add More</Grid>
          </Grid>
        </LinkTag>
      </Grid>
    </Grid>
  );
};

export default SkillsField;
